import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Business,
  Email,
  Phone,
  CalendarToday,
  Description,
  People,
  Assessment,
  NavigateNext,
} from '@mui/icons-material';
import api from '../utils/axios';
import dayjs from 'dayjs';
import { useBreadcrumb } from '../App';

interface Insurer {
  id: string;
  name: string;
  email: string;
  phone: string;
  code?: string;
  createdAt: string;
  updatedAt: string;
  claims?: Claim[];
  representatives?: InsuranceRepresentative[];
}

interface Claim {
  id: string;
  claimNumber: string;
  status: string;
  type: string;
  amount?: number;
  description?: string;
  createdAt: string;
  file_number?: string; // Added file_number
}

interface InsuranceRepresentative {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  title?: string;
  department?: string;
  status: string;
}

const InsurerDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Insurer>>({});
  const { setLastItem } = useBreadcrumb();

  const { data: insurerData, isLoading, error, refetch } = useQuery({
    queryKey: ['insurer', id],
    queryFn: async (): Promise<{ success: boolean; data: Insurer }> => {
      const response = await api.get<{ success: boolean; data: Insurer }>(`/insurers/${id}`);
      return response.data;
    },
    enabled: !!id,
  });

  const insurer = insurerData?.data;

  useEffect(() => {
    if (insurer) {
      setEditForm({
        name: insurer.name,
        email: insurer.email,
        phone: insurer.phone,
        code: insurer.code,
      });
      // Set the insurer name in the breadcrumb
      setLastItem(insurer.name);
    }
    return () => setLastItem(undefined);
  }, [insurer, setLastItem]);

  const handleInputChange = (field: keyof Insurer, value: string) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      await api.put(`/insurers/${id}`, editForm);
      setIsEditDialogOpen(false);
      refetch();
    } catch (error) {
      console.error('Error updating insurer:', error);
    }
  };

  const handleCancel = () => {
    setIsEditDialogOpen(false);
    if (insurer) {
      setEditForm({
        name: insurer.name,
        email: insurer.email,
        phone: insurer.phone,
        code: insurer.code,
      });
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !insurer) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Failed to load insurer details. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/insurers')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1">
          {insurer.name}
        </Typography>
        <Box ml="auto">
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={() => setIsEditDialogOpen(true)}
          >
            Edit Insurer
          </Button>
        </Box>
      </Box>

      <Box display="flex" flexDirection="column" gap={3}>
        <Box display="flex" gap={3} flexWrap="wrap">
          {/* Basic Information */}
          <Box flex="1" minWidth="300px">
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Business sx={{ mr: 1 }} />
                  <Typography variant="h6">Company Information</Typography>
                </Box>
                
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Company Name
                    </Typography>
                    <Typography variant="body1">{insurer.name}</Typography>
                  </Box>
                  
                  {insurer.code && (
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">
                        Company Code
                      </Typography>
                      <Typography variant="body1">{insurer.code}</Typography>
                    </Box>
                  )}
                  
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Email
                    </Typography>
                    <Box display="flex" alignItems="center">
                      <Email sx={{ mr: 1, fontSize: 16 }} />
                      <Typography variant="body1">{insurer.email}</Typography>
                    </Box>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Phone
                    </Typography>
                    <Box display="flex" alignItems="center">
                      <Phone sx={{ mr: 1, fontSize: 16 }} />
                      <Typography variant="body1">{insurer.phone}</Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Statistics */}
          <Box flex="1" minWidth="300px">
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Assessment sx={{ mr: 1 }} />
                  <Typography variant="h6">Statistics</Typography>
                </Box>
                
                <Box display="flex" gap={2}>
                  <Box flex="1" textAlign="center">
                    <Typography variant="h4" color="primary">
                      {insurer.claims?.length || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Claims
                    </Typography>
                  </Box>
                  <Box flex="1" textAlign="center">
                    <Typography variant="h4" color="secondary">
                      {insurer.representatives?.length || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Representatives
                    </Typography>
                  </Box>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Created
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <CalendarToday sx={{ mr: 1, fontSize: 16 }} />
                    <Typography variant="body2">
                      {dayjs(insurer.createdAt).format('MMM DD, YYYY')}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Claims */}
        <Box>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Description sx={{ mr: 1 }} />
                <Typography variant="h6">Claims</Typography>
              </Box>
              
              {insurer.claims && insurer.claims.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Claim Number</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Created</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {insurer.claims.map((claim) => (
                        <TableRow key={claim.id}>
                          <TableCell>
                            {claim.file_number ? (
                              <Button
                                variant="text"
                                onClick={() => navigate(`/claims/${claim.file_number}`)}
                              >
                                {claim.claimNumber}
                              </Button>
                            ) : (
                              <span style={{ color: '#999' }}>
                                {claim.claimNumber} (No file number)
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={claim.status}
                              color={
                                claim.status === 'ACTIVE' ? 'success' :
                                claim.status === 'PENDING' ? 'warning' : 'default'
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{claim.type}</TableCell>
                          <TableCell>
                            {claim.amount ? `$${claim.amount.toLocaleString()}` : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {dayjs(claim.createdAt).format('MM/DD/YYYY')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No claims found for this insurer.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Representatives */}
        <Box>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <People sx={{ mr: 1 }} />
                <Typography variant="h6">Insurance Representatives</Typography>
              </Box>
              
              {insurer.representatives && insurer.representatives.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Title</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {insurer.representatives.map((rep) => (
                        <TableRow key={rep.id}>
                          <TableCell>
                            <Button
                              variant="text"
                              onClick={() => navigate(`/insurance-representatives/${rep.id}`)}
                            >
                              {`${rep.firstName} ${rep.lastName}`}
                            </Button>
                          </TableCell>
                          <TableCell>{rep.email}</TableCell>
                          <TableCell>{rep.phone || 'N/A'}</TableCell>
                          <TableCell>{rep.title || 'N/A'}</TableCell>
                          <TableCell>{rep.department || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip
                              label={rep.status}
                              color={
                                rep.status === 'ACTIVE' ? 'success' :
                                rep.status === 'INACTIVE' ? 'error' : 'warning'
                              }
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No representatives found for this insurer.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Insurer</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Company Name"
              value={editForm.name || ''}
              onChange={(e) => handleInputChange('name', e.target.value)}
              fullWidth
              required
            />
            
            <TextField
              label="Company Code"
              value={editForm.code || ''}
              onChange={(e) => handleInputChange('code', e.target.value)}
              fullWidth
            />
            
            <TextField
              label="Email"
              type="email"
              value={editForm.email || ''}
              onChange={(e) => handleInputChange('email', e.target.value)}
              fullWidth
              required
            />
            
            <TextField
              label="Phone"
              value={editForm.phone || ''}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              fullWidth
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InsurerDetails; 